import { zodResolver } from "@hookform/resolvers/zod";
import type { AssignableRole } from "@unbox/api";
import { merchantScopeRoles } from "@unbox/api";
import { Mail01 } from "@untitledui/icons";
import { useEffect } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import z from "zod/v3";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { TeamRoleSelect } from "./team-roles";

// Add a person to the brand, or change the role of someone already listed. One
// modal for both. The brand is fixed and the only choice is the role, so the
// workspace page (UNB-1958), where brands are multi-select, composes its own
// modal from the shared role/table pieces instead of reusing this one.

const schema = z.object({
  email: z.string().trim().min(1, "Email is required").email("Enter a valid email address"),
  role: z.enum(["user", "merchant_admin"]),
});

export type TeamMemberFormValues = z.infer<typeof schema>;

type TeamMemberModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  merchant: { name: string; logoUrl?: string | null };
  /** Roles the viewer may grant — `usePermissions().invitableRoles`. */
  assignableRoles: AssignableRole[];
  /** The member or pending invite being edited; omit to add someone new. */
  editing?: TeamMemberFormValues | null;
  onSubmit: (values: TeamMemberFormValues) => void;
  isPending?: boolean;
};

export const TeamMemberModal = ({
  isOpen,
  onOpenChange,
  merchant,
  assignableRoles,
  editing,
  onSubmit,
  isPending,
}: TeamMemberModalProps) => {
  const isEditing = !!editing;

  const form = useForm<TeamMemberFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", role: "user" },
  });

  // Reset to the row being edited (or to a blank invite) each time it opens.
  useEffect(() => {
    if (!isOpen) return;
    form.reset(editing ?? { email: "", role: "user" });
  }, [isOpen, editing, form]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      {/* Not dismissable: an outside click would discard the whole form (UNB-2129). */}
      <ModalOverlay isDismissable={false}>
        {/* 640px matches the design and the other invite modals; the base Modal is
            unconstrained, so without a cap it stretches to the overlay. */}
        <Modal className="max-w-[640px]">
          <Dialog>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl transition-all"
            >
              <CloseButton
                onClick={() => onOpenChange(false)}
                theme="light"
                size="lg"
                className="absolute top-3 right-3"
              />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <div className="z-10 flex flex-col gap-0.5">
                  <Heading slot="title" className="text-md font-semibold text-primary">
                    {isEditing ? `Edit access to ${merchant.name}` : `Add new user to ${merchant.name}`}
                  </Heading>
                  <p className="text-sm text-tertiary">
                    {isEditing
                      ? "Change the role this person holds on this brand."
                      : "Invite someone and configure what they can access."}
                  </p>
                </div>
              </div>

              <div className="h-5 w-full" />

              <div className="relative flex flex-col gap-4 px-4 sm:gap-5 sm:px-6">
                <Controller
                  control={form.control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <Input
                      isRequired
                      size="md"
                      label="Email"
                      placeholder="Email"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isDisabled={isEditing}
                      isInvalid={!!fieldState.error}
                      hint={fieldState.error?.message}
                    />
                  )}
                />

                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-secondary">Add to Merchant</span>
                  {/* The logo bleeds to the row's edges, so the row carries no left or
                      vertical padding and clips the corners for it. */}
                  <div className="flex items-center gap-4 overflow-hidden rounded-md border-2 border-brand bg-primary pr-4">
                    <MerchantLogo
                      name={merchant.name}
                      logoUrl={merchant.logoUrl}
                      size="lg"
                      className="size-14 shrink-0 rounded-none ring-0"
                    />
                    <span className="text-sm font-medium text-secondary flex-1 min-w-0 truncate">{merchant.name}</span>
                    <Controller
                      control={form.control}
                      name="role"
                      render={({ field }) => (
                        <TeamRoleSelect
                          aria-label={`Role for ${merchant.name}`}
                          options={merchantScopeRoles}
                          assignableRoles={assignableRoles}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                {!isEditing && (
                  <div className="flex items-center gap-1.5 rounded-xl bg-secondary p-4">
                    <Mail01 className="size-5 shrink-0 text-fg-secondary" />
                    <p className="text-sm font-medium text-secondary">
                      This will auto send an email to the user asking them to set up their account.
                    </p>
                  </div>
                )}
              </div>

              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" color="primary" size="lg" isLoading={isPending}>
                  {isEditing ? "Save changes" : "Add new User"}
                </Button>
              </div>
            </form>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
